/**
 * TierContext - Global tier and user state management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';

const TierContext = createContext(null);

export const TierProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Fetch user and tier info
  const fetchUserData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [userRes, tierRes] = await Promise.all([
        authAPI.getCurrentUser(),
        userAPI.getTierInfo(),
      ]);

      setUser(userRes.data);
      setTierInfo(tierRes.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    
    // Listen for user data change events (e.g., after email verification)
    const handleUserDataChange = () => {
      fetchUserData();
    };
    
    window.addEventListener('user-data-changed', handleUserDataChange);
    
    return () => {
      window.removeEventListener('user-data-changed', handleUserDataChange);
    };
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setTierInfo(null);
  };

  const refreshTierInfo = async () => {
    try {
      const tierRes = await userAPI.getTierInfo();
      setTierInfo(tierRes.data);
    } catch (error) {
      console.error('Failed to refresh tier info:', error);
    }
  };

  const value = {
    user,
    tierInfo,
    loading,
    isAuthenticated: !!token,
    isFree: tierInfo?.tier === 'free',
    isPremium: tierInfo?.tier === 'premium',
    login,
    logout,
    refreshTierInfo,
  };

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>;
};

export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within TierProvider');
  }
  return context;
};
